import React from "react";
import styled from "styled-components";
import { APP_NAME } from "../../brand";

/** Letters alternate ink/paper, and each one is stroked in the other colour. */
const LETTER_FILLS = ["var(--ink)", "var(--paper)"];

const Mark = styled.div<{ $size: "lg" | "sm" }>`
  --mark-size: ${({ $size }) => ($size === "lg" ? "var(--text-display)" : "var(--text-xl)")};
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  user-select: none;
`;

const Row = styled.h1`
  display: flex;
  align-items: center;
  gap: 0.06em;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: var(--mark-size);
  line-height: 1;
  margin: 0;
`;

const Letter = styled.span<{ $fill: string; $stroke: string; $tilt: number; $i: number }>`
  display: inline-block;
  color: ${({ $fill }) => $fill};
  -webkit-text-stroke: 0.1em ${({ $stroke }) => $stroke};
  paint-order: stroke fill;
  text-shadow: 0 0.07em 0 rgb(0 0 0 / 0.25);
  rotate: ${({ $tilt }) => $tilt}deg;
  animation: pop-in var(--dur-slow) var(--ease-bounce) both;
  animation-delay: ${({ $i }) => $i * 45}ms;

  @media (hover: hover) {
    &:hover {
      animation: wiggle 500ms var(--ease-out);
    }
  }
`;

const Squiggle = styled.svg`
  width: 70%;
  height: 0.3em;
  font-size: var(--mark-size);
  overflow: visible;

  path {
    fill: none;
    stroke: var(--gold);
    stroke-width: 5;
    stroke-linecap: round;
    stroke-dasharray: 260;
    stroke-dashoffset: 260;
    animation: draw-in 900ms var(--ease-out) 350ms forwards;
  }
`;

type WordmarkProps = { size?: "lg" | "sm" };

/** "Pesadown" in crayon sticker letters, alternating ink and paper. */
const Wordmark: React.FC<WordmarkProps> = ({ size = "lg" }) => (
  <Mark $size={size}>
    <Row aria-label={APP_NAME}>
      {APP_NAME.split("").map((char, i) => (
        <Letter
          key={i}
          aria-hidden
          $i={i}
          $fill={LETTER_FILLS[i % 2]}
          $stroke={LETTER_FILLS[(i + 1) % 2]}
          $tilt={i % 2 ? 4 : -4}
        >
          {char}
        </Letter>
      ))}
    </Row>
    {size === "lg" && (
      <Squiggle viewBox="0 0 240 16" preserveAspectRatio="none" aria-hidden>
        <path d="M4 10 C 24 2, 44 16, 64 8 S 104 2, 124 9 S 164 15, 184 7 S 224 3, 236 9" />
      </Squiggle>
    )}
  </Mark>
);

export default Wordmark;
